import {
  get,
  find,
  isArray,
  sortBy,
  map,
  each,
  isNil,
  isString,
  groupBy,
  keys,
  isNaN,
  omit,
  findIndex,
  round,
  isEmpty,
  every,
  isEqual,
  has,
  forEach,
  filter,
  invoke,
} from 'lodash';
import emojiRegex from 'emoji-regex/RGI_Emoji';

import {
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERICAL_OPEN_ENDED,
  QUESTION_RANKING,
  QUESTION_NPS,
  QUESTION_RATING,
  QUESTION_SLIDER,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_VAN_WESTENDORP,
  ANSWER_OTHER,
  SUPPORTED_LANGUAGES,
  BLANK_PIPING_ANSWER,
  GROUP_TYPE_CONJOINT,
  QUESTION_MATRIX_MAXDIFF,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_DRILLDOWN,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_OPEN_ENDED_NUMERIC,
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
  QUESTION_CONVERSATIONAL,
} from './constants';
import { getOrderedSelectedLevels } from './reactComponents/QuestionBody/DrilldownQuestion/utils';
import {
  KnowledgeKeys,
  ActionVisibilityValueType,
  OutcomeType,
  VisibilityService,
  carryForwardService,
  shuffleArray,
  getAnswersShuffleType,
  getScorepointShuffleType,
  getGenericShuffleType,
  getShuffledAndCarriedForwardAnswers,
  base64,
  getDecodedText,
  enrichMatrixRows,
  isMatrix,
  isMatrixTypeLikert,
  isMatrixOpenEndedFamily,
  isNodeSupportAnswersVisibility,
  getNodeVisibility,
  VisibilityStatus,
} from './logic';

export const checkIsPastTimestamp = (timestamp) => {
  if (!timestamp || isNaN(timestamp)) {
    return true;
  }
  return Date.now() >= timestamp;
};

/* eslint-disable no-bitwise */
export const toUTF8Array = function toUTF8Array(str) {
  const utf8 = [];

  for (let i = 0; i < str.length; i += 1) {
    let charcode = str.charCodeAt(i);
    if (charcode < 0x80) utf8.push(charcode);
    else if (charcode < 0x800) {
      utf8.push(0xc0 | (charcode >> 6), 0x80 | (charcode & 0x3f));
    } else if (charcode < 0xd800 || charcode >= 0xe000) {
      utf8.push(
        0xe0 | (charcode >> 12),
        0x80 | ((charcode >> 6) & 0x3f),
        0x80 | (charcode & 0x3f),
      );
    } else {
      i += 1;
      charcode =
        0x10000 + (((charcode & 0x3ff) << 10) | (str.charCodeAt(i) & 0x3ff));
      utf8.push(
        0xf0 | (charcode >> 18),
        0x80 | ((charcode >> 12) & 0x3f),
        0x80 | ((charcode >> 6) & 0x3f),
        0x80 | (charcode & 0x3f),
      );
    }
  }
  return utf8;
};

// There is an issue where an answer is open-ended or other
// the text may contains " which is equal to \" character
// will produce an error in backend due to encoding is being done in mobile,
// example bellow with fail.
// const text = 'Pollfish"';
// const encoded = encodeURIComponent(text);        // 'Pollfish%22'
// const stringified = JSON.stringify(encoded);     // '"Pollfish%22"'
// const decoded = decodeURIComponent(stringified); // '"Pollfish""'
// const parsed = JSON.parse(decoded);              // will produce an error
// We should escape backslash (\) with an extra backslash (\\).
// In order for the backend to not produce an error.
export const escapeJSONSpecialChars = (text) =>
  text
    .replace(/[\\]/g, '\\\\')
    .replace(/["]/g, '\\"')
    .replace(/[\b]/g, '\\b')
    .replace(/[\f]/g, '\\f')
    .replace(/[\n]/g, '\\n')
    .replace(/[\r]/g, '\\r')
    .replace(/[\t]/g, '\\t');

export const encodeTextAnswers = (requestObject) => {
  const { answers } = requestObject;

  return {
    ...requestObject,
    answers: answers.map((answer) => {
      if (answer.text) {
        return {
          ...answer,
          text: encodeURIComponent(escapeJSONSpecialChars(answer.text)),
        };
      }

      // fixes stringValues containing special characters like "&". This fix is needed as long as we send the
      // JSON data format on the url instead of a POST body
      if (answer.matrixColumns) {
        const transformedMatrixColumns = answer.matrixColumns.map(
          (matrixColumn) => {
            if (matrixColumn.stringValue) {
              return {
                ...matrixColumn,
                stringValue: encodeURIComponent(
                  escapeJSONSpecialChars(matrixColumn.stringValue),
                ),
              };
            }
            return matrixColumn;
          },
        );
        return {
          ...answer,
          matrixColumns: transformedMatrixColumns,
        };
      }

      return answer;
    }),
  };
};

// given an object it will transform all values to a string
export const transformValuesToString = (object) => {
  const transformedJSON = {};
  Object.keys(object).forEach((key) => {
    const value = object[key];
    if (isArray(value)) {
      transformedJSON[key] = value;
    } else if (value !== undefined && value !== null) {
      if (typeof value === 'object') {
        transformedJSON[key] = transformValuesToString(value);
      } else {
        transformedJSON[key] = value.toString();
      }
    }
  });
  return transformedJSON;
};
/* eslint-enable no-bitwise */

export const getRandomBoolean = () => Math.random() >= 0.5;

export const getLanguageForKey = (key) => {
  const lang = find(SUPPORTED_LANGUAGES, ['key', key]);
  return get(lang, 'value', '');
};

const getCarriedForwardAnswerText = ({ answer, carryForward }) => {
  const respondentKnowledge = Pollfish.store.getRespondentKnowledge();
  const {
    relevantRespondentKnowledge,
    // relevantCarryForward,
  } = carryForwardService.getRelevantCarryForwardData(
    answer,
    carryForward,
    respondentKnowledge,
  );
  if (!relevantRespondentKnowledge) return null;

  const {
    answers: relevantRespondentAnswers = [],
  } = relevantRespondentKnowledge;
  const originalRefId =
    answer.originalAnswerRefId || answer.originalScorepointRefId;
  let relevantRespondentAnswer;

  const sourceQuestion = find(Pollfish.survey.getQuestions(), [
    'refId',
    answer.questionRefId,
  ]);
  const sourceQuestionType = get(sourceQuestion, 'style');

  if (
    [
      QUESTION_OPEN_ENDED,
      QUESTION_NUMERICAL_OPEN_ENDED,
      QUESTION_SLIDER,
    ].includes(sourceQuestionType)
  ) {
    // extract first (and single) respondent knowledge answer
    [relevantRespondentAnswer] = relevantRespondentAnswers;
  } else {
    relevantRespondentAnswer = find(relevantRespondentAnswers, [
      'id',
      originalRefId,
    ]);
  }

  if (!relevantRespondentAnswer) return null;

  if (
    answer.isCarriedForward &&
    relevantRespondentAnswer.text !== answer.body
  ) {
    return relevantRespondentAnswer.text;
  }
  return null;
};

const getCarriedForwardScorepointText = ({ scorepoint, carryForward }) => {
  const respondentKnowledge = Pollfish.store.getRespondentKnowledge();
  const {
    relevantRespondentKnowledge,
    // relevantCarryForward,
  } = carryForwardService.getRelevantCarryForwardData(
    scorepoint,
    carryForward,
    respondentKnowledge,
  );

  if (!relevantRespondentKnowledge) return null;

  const {
    answers: relevantRespondentAnswers = [],
  } = relevantRespondentKnowledge;
  let relevantRespondentAnswer;
  const sourceQuestion = find(Pollfish.survey.getQuestions(), [
    'refId',
    scorepoint.questionRefId,
  ]);
  const sourceQuestionType = get(sourceQuestion, 'style');

  if (
    [
      QUESTION_OPEN_ENDED,
      QUESTION_NUMERICAL_OPEN_ENDED,
      QUESTION_SLIDER,
    ].includes(sourceQuestionType)
  ) {
    // extract first (and single) respondent knowledge answer
    [relevantRespondentAnswer] = relevantRespondentAnswers;
  } else {
    relevantRespondentAnswer = find(relevantRespondentAnswers, [
      'id',
      scorepoint.originalAnswerRefId,
    ]);
  }

  if (!relevantRespondentAnswer) return null;

  if (
    scorepoint.isCarriedForward &&
    relevantRespondentAnswer.text !== scorepoint.body
  ) {
    return relevantRespondentAnswer.text;
  }
  return null;
};

export function updatedAnswersIfOriginalWasDynamicValue({
  answer,
  response,
  question,
}) {
  const updatedAnswer = { ...answer };
  // update the answers body if the original was dynamic value based
  if (response.text) {
    const text = getDecodedText(response.text);
    if (isMatrixOpenEndedFamily(question)) {
      updatedAnswer.beforeLabel = text;
    } else {
      updatedAnswer.body = text;
    }
  }
  return updatedAnswer;
}

function isMatrixAnswerEmpty(answerResponse) {
  if (isEmpty(answerResponse.matrixColumns)) {
    return true;
  }
  // TODO: we currently only assume booleanType values. This should change in the future
  const isBooleanTypeAnswer = true;
  if (isBooleanTypeAnswer) {
    const isEveryColumnFalse = every(
      answerResponse.matrixColumns,
      (column) => column.booleanValue === false,
    );
    return isEveryColumnFalse;
  }

  // eslint-disable-next-line no-console
  console.warn('Missing implementation');
  return false;
}

// NOTES: this function both validates AND transforms answers due to different needs.
//        I dont think it is the best idea architectural wise to do both but it will be helpful
//        until we have a clear view of what kind of validations and transformations the backend
//        will require for the new matrix.
function validateAndTransformMatrixAnswers(question, answers) {
  const capabilities = get(question, 'matrix.capabilities', {});
  let newAnswers = answers;

  // the backend does not want us to send them empty choices. This is implemented for the MaxDiff
  // question/group type. Be careful if you need to change this to also update maxDiff
  if (capabilities.allowOptionalRows) {
    newAnswers = newAnswers.filter(
      (answerResponse) => !isMatrixAnswerEmpty(answerResponse),
    );
  }

  // not sure how we should handle these type of validations gracefuly on the UI.
  // Sentry is enough for now to inform us in case this happens
  if (capabilities.requireColumnOnce) {
    const columns = get(question, 'matrix.columns', []);
    const requiredColumnIds = columns.map((column) => column.id.toString());
    const selectedColumnIds = [];
    newAnswers.forEach((answerResponse) => {
      if (question.style === QUESTION_DRILLDOWN) {
        answerResponse.matrixColumns.forEach((column) => {
          if (has(column, 'drilldownValue')) {
            selectedColumnIds.push(column.columnId);
          }
        });
      } else {
        const selectedColumn = find(answerResponse.matrixColumns, [
          'booleanValue',
          true,
        ]);
        if (selectedColumn) {
          selectedColumnIds.push(selectedColumn.columnId);
        }
      }
    });

    // we need to sort the arrays because isEqual([columnId1, columndId2], [columnId2, columnId1])
    // will throw an error
    if (
      !isEqual(
        sortBy(requiredColumnIds, (id) => id),
        sortBy(selectedColumnIds, (id) => id),
      )
    ) {
      Raven.captureException(
        'Required columns do not match selected columns for matrix',
        {
          extra: {
            survey_id: Pollfish.helper.json.s_id,
            requiredColumnIds,
            selectedColumnIds,
            question,
            answers,
          },
        },
      );
    }
  }
  return newAnswers;
}

export const transformAnswersForBackendStorage = ({ question, answers }) => {
  const questionRefId = question.refId || question.id;
  const questionWrapper = Pollfish.ui.getQuestionWrapperElement(question);
  const transformedAnswers = [];
  const { carryForward = [] } = question;
  // iterate through all selected answer ids and handle custom question types
  map(answers, (answer, ind) => {
    const answerOrder = findIndex(
      Pollfish.store.getShuffledAnswers(questionRefId),
      ['id', answer.id],
    );
    const answerResponse = {
      id: answer.id,
    };
    answerResponse.answerOrder = answerOrder.toString();
    if (carryForward.length) {
      const carriedAnswerText = getCarriedForwardAnswerText({
        answer,
        carryForward,
      });

      if (carriedAnswerText) {
        const carriedText = get(answerResponse, 'carriedText', {});
        carriedText.answer = { id: answer.id, text: carriedAnswerText };
        answerResponse.carriedText = carriedText;
      }
    }
    const group = Pollfish.helper.getQuestionGroup(question);
    if (group && group.type === GROUP_TYPE_CONJOINT) {
      // TODO: handle this if/when needed here
    } else if (question.style === QUESTION_OPEN_ENDED) {
      // get trimmed value of open-ended
      answerResponse.text = Pollfish.helper.getTextFromField(
        `#freeText-${questionRefId}`,
      );
      // if it is numerical open-ended retrieve the written value
    } else if (question.style === QUESTION_NUMERICAL_OPEN_ENDED) {
      const value = Pollfish.helper.getNumericValue(questionRefId);
      answerResponse.text = value;
      // if it is ranking get the selected rank
    } else if (question.style === QUESTION_RANKING) {
      const answerElement = $(questionWrapper).find('.answerRanking')[ind];
      const targetId = $(answerElement).data('id');

      const rank = $(answerElement).attr('data-selected-rank');
      answerResponse.id = targetId;
      answerResponse.text = rank;
      // if it is matrix get the selected scorepoint
    } else if (question.style === QUESTION_MATRIX_SINGLE) {
      const answerElement = $(`.answerMatrix[data-answer-id=${answer.id}]`);
      const rowOrder = answerElement.index();
      const selectedColumnId = answerElement.attr(
        'data-selected-scorepoint-id',
      );
      const columnElement = answerElement.find(
        `[data-scorepoint-id=${selectedColumnId}]`,
      );
      const columnOrder = columnElement.index();
      answerResponse.id = answer.id;
      answerResponse.text = selectedColumnId;
      answerResponse.answerOrder = rowOrder.toString();
      answerResponse.textOrder = [columnOrder.toString()];

      if (carryForward.length) {
        const scorepointId = Number(selectedColumnId);

        const { scorepoints = [] } = question;
        const scorepoint = find(scorepoints, ['id', scorepointId]);
        const carriedForwardScorepointText = getCarriedForwardScorepointText({
          scorepoint,
          carryForward,
        });

        if (carriedForwardScorepointText) {
          const carriedText = get(answerResponse, 'carriedText', {});
          const { scorepoints: carriedScorepointsWithText = [] } = carriedText;
          carriedScorepointsWithText.push({
            id: scorepoint.id,
            text: carriedForwardScorepointText,
          });
          carriedText.scorepoints = carriedScorepointsWithText;
          answerResponse.carriedText = carriedText;
        }
      }
    } else if (question.style === QUESTION_MATRIX_MULTIPLE) {
      const answerElement = $(`.answerMatrix[data-answer-id=${answer.id}]`);
      const rowOrder = answerElement.index();
      const selectedScorepointIds = answerElement.attr(
        'data-selected-scorepoint-id',
      );
      const columnOrder = [];
      selectedScorepointIds.split(',').forEach((scorepointId) => {
        const scorepointElement = answerElement.find(
          `[data-scorepoint-id=${scorepointId}]`,
        );
        columnOrder.push(scorepointElement.index().toString());
      });
      answerResponse.id = answer.id;
      answerResponse.text = selectedScorepointIds;
      answerResponse.answerOrder = rowOrder.toString();
      answerResponse.textOrder = columnOrder;

      if (carryForward.length) {
        const scorepointIds = selectedScorepointIds.split(',').map(Number);

        scorepointIds.forEach((scorepointId) => {
          const { scorepoints = [] } = question;
          const scorepoint = find(scorepoints, ['id', scorepointId]);
          const carriedForwardScorepointText = getCarriedForwardScorepointText({
            scorepoint,
            carryForward,
          });

          if (carriedForwardScorepointText) {
            const carriedText = get(answerResponse, 'carriedText', {});
            const {
              scorepoints: carriedScorepointsWithText = [],
            } = carriedText;
            carriedScorepointsWithText.push({
              id: scorepoint.id,
              text: carriedForwardScorepointText,
            });
            carriedText.scorepoints = carriedScorepointsWithText;
            answerResponse.carriedText = carriedText;
          }
        });
      }
    } else if (question.style === QUESTION_SLIDER) {
      // [RES-4943] we send this for d.rules back calculations convenience
      answerResponse.text = answer.body;
    } else if (question.style === QUESTION_NPS) {
      // [RES-4943] we send this for d.rules back calculations convenience
      // parseInt helps handle cases with text e.g. 0 - Not likely
      answerResponse.text = String(parseInt(answer.body, 10));
    } else if (question.style === QUESTION_RATING) {
      // [RES-4943] we send this for d.rules back calculations convenience
      answerResponse.text = answer.body;
    } else if (question.style === QUESTION_MATRIX_VAN_WESTENDORP) {
      const answerElement = $(
        `.answerMatrixVanWestendorp[data-answer-id=${answer.id}]`,
      );
      const answerColumnId = $(answerElement).attr('data-answer-column-id');
      const answerColumnOrder = $(answerElement).attr(
        'data-answer-column-order',
      );
      const answerValue = answerElement.find('.input-van-westendorp').val();
      answerResponse.text = answerValue.toString();
      answerResponse.matrixColumns = [
        {
          columnId: answerColumnId.toString(),
          valueOrder: parseInt(answerColumnOrder, 10),
          numericValue: parseFloat(answerValue),
        },
      ];
    } else if (question.style === QUESTION_MATRIX_MAXDIFF) {
      const answerElement = $(`#${answer.id}.answerClick`);
      const isBestSelection = $(answerElement).data('answer-best') || false;
      const isWorstSelection = $(answerElement).data('answer-worst') || false;

      const columns = get(question, 'matrix.columns');
      const bestColumn = columns[0];
      const worstColumn = columns[1];

      answerResponse.matrixColumns = [];
      answerResponse.matrixColumns.push({
        columnId: bestColumn.id.toString(),
        valueOrder: 0,
        booleanValue: isBestSelection,
      });
      answerResponse.matrixColumns.push({
        columnId: worstColumn.id.toString(),
        valueOrder: 1,
        booleanValue: isWorstSelection,
      });
      let text = answer.body;
      if (isBestSelection) {
        text += `:${bestColumn.beforeLabel}`;
      } else if (isWorstSelection) {
        text += `:${worstColumn.beforeLabel}`;
      }
      answerResponse.text = text;
      // if it is matrix bipolar get the selected column
    } else if (question.style === QUESTION_MATRIX_BIPOLAR) {
      const answerElement = $(`#${answer.id}.answerClick`);
      const answerColumnId = $(answerElement).attr('data-selected-column-id');
      const answerColumnOrder = $(answerElement).attr(
        'data-selected-column-order',
      );
      const columns = get(question, 'matrix.columns', []);
      const column = find(columns, ['id', parseInt(answerColumnId, 10)]);
      answerResponse.matrixColumns = [
        {
          columnId: answerColumnId.toString(),
          valueOrder: parseInt(answerColumnOrder, 10),
          index: column.order,
          booleanValue: true,
        },
      ];

      // check if answer is open-ended and send text as well
    } else if (question.style === QUESTION_DRILLDOWN) {
      // answer.id
      const levels = get(
        question,
        'matrix.validation.drillDownConfiguration.config.levels',
        [],
      );
      const orderedSelectedLevels = getOrderedSelectedLevels(
        levels,
        answer.refId,
      );
      answerResponse.matrixColumns = [];
      question.matrix.columns.forEach((column, index) => {
        const selectedValue = orderedSelectedLevels[index];
        answerResponse.matrixColumns.push({
          columnId: column.id.toString(),
          stringValue: selectedValue.value,
          valueOrder: selectedValue.order,
          drilldownValue: selectedValue.refId,
        });
      });
    } else if (
      question.style === QUESTION_MATRIX_OPEN_ENDED ||
      question.style === QUESTION_MATRIX_OPEN_ENDED_NUMERIC ||
      question.style === QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM
    ) {
      answerResponse.matrixColumns = [];
      const answerColumns = $(
        `.answer--matrix-open-ended-answer-input[data-row-id=${answer.id}]`,
      );
      each(answerColumns, (domElement) => {
        if ($(domElement).val() !== '') {
          const columnId = $(domElement).attr('data-column-id');
          const columnOrder = $(domElement).attr('data-column-order');
          const answerValue = $(domElement).val();
          const property =
            question.style === QUESTION_MATRIX_OPEN_ENDED
              ? 'stringValue'
              : 'numericValue';
          const value =
            question.style === QUESTION_MATRIX_OPEN_ENDED
              ? answerValue
              : parseFloat(answerValue);

          answerResponse.matrixColumns.push({
            columnId: columnId.toString(),
            valueOrder: parseInt(columnOrder, 10),
            [property]: value,
          });
        }
      });
    } else if (question.style === QUESTION_CONVERSATIONAL) {
      answerResponse.matrixColumns = [];
      const answerColumns = $(
        `.answer--conversational-ai[data-row-id=${answer.id}]`,
      );
      each(answerColumns, (domElement) => {
        const answerValue = $(domElement).attr('data-value');
        if (answerValue !== '') {
          const columnId = $(domElement).attr('data-column-id');
          const columnOrder = $(domElement).attr('data-column-order');
          answerResponse.matrixColumns.push({
            columnId: columnId.toString(),
            valueOrder: parseInt(columnOrder, 10),
            stringValue: answerValue,
          });
        }
      });
    } else if (answer.style === ANSWER_OTHER) {
      answerResponse.text = Pollfish.helper.getTextFromField(
        `#otherContainerInput-${questionRefId}`,
      );
    }
    // text property is always expected to be a string
    if (!isNil(answerResponse.text) && !isString(answerResponse.text)) {
      answerResponse.text = answerResponse.text.toString();
    }

    transformedAnswers.push(answerResponse);
  });

  const capabilities = get(question, 'matrix.capabilities', {});
  if (!isEmpty(capabilities)) {
    return validateAndTransformMatrixAnswers(question, transformedAnswers);
  }
  return transformedAnswers;
};

export function transformConceptsForBackendStorage(answeredConcepts = []) {
  const attributeGroupingCombinations = [];
  forEach(answeredConcepts, (answeredConcept) => {
    attributeGroupingCombinations.push({
      question_group_ref_id: answeredConcept.nodeId,
      attribute_grouping_ids: answeredConcept.conceptBackEndIds,
    });
  });
  return attributeGroupingCombinations;
}

export const transformAnswersForADL = ({ question, answers }) => {
  const questionRefId = question.refId || question.id;
  const transformedAnswers = [];
  // iterate through all selected answer ids and handle custom question types
  map(answers, (answer) => {
    const answerResponse = {
      id: answer.refId,
    };
    // if it is open-ended handle android sdk < 12 non-latin characters
    if (question.style === QUESTION_OPEN_ENDED) {
      // get trimmed value of open-ended
      answerResponse.text = Pollfish.helper.getTextFromField(
        `#freeText-${questionRefId}`,
      );
      // if it is numerical open-ended retrieve the written value
    } else if (question.style === QUESTION_NUMERICAL_OPEN_ENDED) {
      const value = Pollfish.helper.getNumericValue(questionRefId);
      answerResponse.text = value;
      // if it is ranking get the selected rank
    } else if (question.style === QUESTION_RANKING) {
      const answerElement = $(`.answerRanking[data-id="${answer.id}"]`);
      const rank = $(answerElement).attr('data-selected-rank');
      answerResponse.row = answer.refId;
      answerResponse.column = rank;
    } else if (
      question.style === QUESTION_NPS ||
      question.style === QUESTION_SLIDER ||
      question.style === QUESTION_RATING
    ) {
      answerResponse.text = parseInt(answer.body, 10);
      // if it is matrix get the selected scorepoint
    } else if (question.style === QUESTION_MATRIX_SINGLE) {
      const answerElement = $(`.answerMatrix[data-answer-id=${answer.id}]`);
      const selectedColumnTextId = answerElement.attr(
        'data-selected-scorepoint-id',
      );
      const selectedColumnId = parseInt(selectedColumnTextId, 10);
      const column = find(question.scorepoints, ['id', selectedColumnId]);
      answerResponse.row = answer.refId;
      answerResponse.column = column.refId;
    } else if (question.style === QUESTION_MATRIX_MULTIPLE) {
      const answerElement = $(`.answerMatrix[data-answer-id=${answer.id}]`);
      const selectedColumnTextIds = answerElement
        .attr('data-selected-scorepoint-id')
        .split(',');

      // we flat map an answer from { row: 'answer.refId', column: 'scorePoint1, scorepoint2' }
      // to multiple separate answers so that we can evaluate them in our ADL as separate units
      each(selectedColumnTextIds, (scorepointTextId) => {
        const selectedColumnId = parseInt(scorepointTextId, 10);
        const scorepoint = find(question.scorepoints, ['id', selectedColumnId]);
        const multiAnswerResponse = {
          row: answer.refId,
          column: scorepoint.refId,
        };
        transformedAnswers.push(multiAnswerResponse);
      });

      return;
      // check if answer is van westendorp and send text as well
    } else if (question.style === QUESTION_MATRIX_VAN_WESTENDORP) {
      const answerElement = $(
        `.answerMatrixVanWestendorp[data-answer-id=${answer.id}]`,
      );
      const answerColumnId = $(answerElement).attr('data-answer-column-id');
      const answerColumnOrder = $(answerElement).attr(
        'data-answer-column-order',
      );
      const answerValue = answerElement.find('.input-van-westendorp').val();
      answerResponse.text = answerValue.toString();
      answerResponse.matrixColumns = [
        {
          columnId: answerColumnId.toString(),
          valueOrder: parseInt(answerColumnOrder, 10),
          numericValue: parseFloat(answerValue),
        },
      ];
      // if it is matrix bipolar get the selected column
    } else if (question.style === QUESTION_MATRIX_BIPOLAR) {
      const answerElement = $(`#${answer.id}.answerClick`);
      const answerColumnId = $(answerElement).attr('data-selected-column-id');
      const columns = get(question, 'matrix.columns', []);
      const column = find(columns, ['id', parseInt(answerColumnId, 10)]);
      answerResponse.row = answer.refId;
      answerResponse.column = column.refId;
      answerResponse.text = column.order.toString();
      // check if answer is open-ended and send text as well
    } else if (question.style === QUESTION_DRILLDOWN) {
      // answer.id
      const levels = get(
        question,
        'matrix.validation.drillDownConfiguration.config.levels',
        [],
      );
      const orderedSelectedLevels = getOrderedSelectedLevels(
        levels,
        answer.refId,
      );
      question.matrix.columns.forEach((column, index) => {
        const selectedValue = orderedSelectedLevels[index];
        transformedAnswers.push({
          ...answerResponse,
          text: selectedValue.value,
        });
      });
      return;
    } else if (
      question.style === QUESTION_MATRIX_OPEN_ENDED ||
      question.style === QUESTION_MATRIX_OPEN_ENDED_NUMERIC ||
      question.style === QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM
    ) {
      const answerColumns = $(
        `.answer--matrix-open-ended-answer-input[data-row-id=${answer.id}]`,
      );
      each(answerColumns, (domElement) => {
        if ($(domElement).val() !== '') {
          const rowRefId = $(domElement).attr('data-row-ref-id');
          const columnRefId = $(domElement).attr('data-column-ref-id');
          const answerValue = $(domElement).val();
          transformedAnswers.push({
            row: rowRefId.toString(),
            column: columnRefId.toString(),
            text: answerValue.toString(),
          });
        }
      });
      return;
    } else if (question.style === QUESTION_CONVERSATIONAL) {
      const answerColumns = $(
        `.answer--conversational-ai[data-row-id=${answer.id}]`,
      );
      each(answerColumns, (domElement) => {
        const answerValue = $(domElement).attr('data-value');
        if (answerValue !== '') {
          const rowRefId = $(domElement).attr('data-row-ref-id');
          const columnRefId = $(domElement).attr('data-column-ref-id');
          transformedAnswers.push({
            row: rowRefId.toString(),
            column: columnRefId.toString(),
            text: answerValue.toString(),
          });
        }
      });
      return;
    } else if (answer.style === ANSWER_OTHER) {
      answerResponse.text = Pollfish.helper.getTextFromField(
        `#otherContainerInput-${questionRefId}`,
      );
    }

    // text property is always expected to be a string
    if (!isNil(answerResponse.text) && !isString(answerResponse.text)) {
      answerResponse.text = answerResponse.text.toString();
    }

    transformedAnswers.push(answerResponse);
  });

  return transformedAnswers;
};

const getAllValuesFromAnswers = ({
  storedQuestion,
  style,
  targetedAnswerRefId,
  userAnswers,
  answers,
  scorepoints,
  matrix,
  backendStoredAnswers,
}) => {
  const values = [];

  switch (style) {
    case QUESTION_RANKING:
    case QUESTION_MATRIX_SINGLE: {
      const targetedAnswers = filter(
        userAnswers,
        (answer) => answer.row === targetedAnswerRefId || !targetedAnswerRefId,
      );
      each(targetedAnswers, (item) => {
        const { row, column } = item;
        const originalAnswer = find(answers, (answer) => answer.refId === row);
        let { body } = originalAnswer;

        if (originalAnswer.isCarriedForward) {
          body = getCarriedForwardUserText({
            originalAnswer,
            backendStoredAnswers,
          });
        }

        let selectedColumn = column;
        if (style === QUESTION_MATRIX_SINGLE) {
          const originalScorepoint = find(
            scorepoints,
            (scorepoint) => scorepoint.refId === column,
          );
          selectedColumn = originalScorepoint.body;
          if (originalScorepoint.isCarriedForward) {
            selectedColumn = getCarriedForwardUserText({
              originalAnswer: originalScorepoint,
              backendStoredAnswers,
            });
          }
        }

        values.push(`${body}: ${selectedColumn}`);
      });

      break;
    }
    case QUESTION_MATRIX_MULTIPLE: {
      const targetedAnswers = filter(
        userAnswers,
        (answer) => answer.row === targetedAnswerRefId || !targetedAnswerRefId,
      );
      const groupedByRow = groupBy(targetedAnswers, 'row');

      // When recalling answer from questions which has shuffle
      // we need to sort the answers by th order which user has seen
      // them when answering, this information only exists in the
      // Pollfish.survey.getStoredAnswers(). So we merge this information
      // with respondent knowledge to show the correct order of answers in recall
      each(keys(groupedByRow), (row) => {
        const currentAnswer = find(answers, (answer) => answer.refId === row);
        const storedAnswer = find(
          storedQuestion.answers,
          (answer) => answer.id === currentAnswer.id,
        );
        const storedText = decodeURIComponent(get(storedAnswer, 'text', ''));
        const storedSelectedScorepoints = storedText.split(',').map(Number);
        const storedTextOrder = get(storedAnswer, 'textOrder', []);
        const storedSelectedScorepointsOrder = storedTextOrder.map(Number);
        const storedScorepointsAndOrder = storedSelectedScorepoints.map(
          (value, index) => ({
            id: value,
            order: storedSelectedScorepointsOrder[index],
          }),
        );

        let selectedColumns = [];

        each(groupedByRow[row], ({ column }) => {
          const scorePoint =
            find(scorepoints, (scorepoint) => scorepoint.refId === column) ||
            {};
          const storedScorepoint =
            find(
              storedScorepointsAndOrder,
              (scorepoint) => scorepoint.id === scorePoint.id,
            ) || {};
          let columnBody = scorePoint.body;
          if (scorePoint.isCarriedForward) {
            columnBody = getCarriedForwardUserText({
              originalAnswer: scorePoint,
              backendStoredAnswers,
            });
          }
          selectedColumns.push({
            body: columnBody,
            index: storedScorepoint.order,
          });
        });
        selectedColumns = sortBy(selectedColumns, 'index').map(
          ({ body }) => body,
        );
        let answerBody = currentAnswer.body;
        if (currentAnswer.isCarriedForward) {
          answerBody = getCarriedForwardUserText({
            originalAnswer: currentAnswer,
            backendStoredAnswers,
          });
        }
        values.push(`${answerBody}: ${selectedColumns.join(', ')}`);
      });

      break;
    }
    case QUESTION_MATRIX_VAN_WESTENDORP: {
      const targetedAnswer = find(
        userAnswers,
        (answer) => answer.id === targetedAnswerRefId,
      );
      if (targetedAnswer) {
        values.push(targetedAnswer.text);
      } else {
        each(userAnswers, (item) => {
          values.push(item.text);
        });
      }

      break;
    }
    case QUESTION_MATRIX_BIPOLAR: {
      const targetedAnswers = filter(
        userAnswers,
        (answer) => answer.row === targetedAnswerRefId || !targetedAnswerRefId,
      );

      each(targetedAnswers, (targetAnswer) => {
        const answer = find(answers, ['refId', targetAnswer.id]) || {};
        const { beforeLabel, afterLabel } =
          find(
            get(matrix, 'matrixAnswers', []),
            (matrixAnswer) => matrixAnswer.answerId === answer.id,
          ) || {};
        const { text = '' } = targetAnswer;
        let fullLabel = beforeLabel;
        if (afterLabel) {
          fullLabel += ` | ${afterLabel}`;
        }
        values.push(`${fullLabel}: ${text}`);
      });

      break;
    }
    default:
      each(userAnswers, (item) => {
        const { id: answerRefId, text } = item;

        // text attribute expected not to be empty string
        if (text) {
          values.push(htmlAsText(text));
        } else {
          const originalAnswer = find(
            answers,
            (answer) => answer.refId === answerRefId,
          );
          let { body } = originalAnswer;

          if (originalAnswer.isCarriedForward) {
            body = getCarriedForwardUserText({
              originalAnswer,
              backendStoredAnswers,
            });
          }
          values.push(body);
        }
      });

      break;
  }

  return values.join(', ');
};

function getCarriedForwardUserText({ originalAnswer, backendStoredAnswers }) {
  const { questionRefId, originalAnswerRefId } = originalAnswer;

  const sourceQuestion = Pollfish.survey.getQuestionByRefId(questionRefId);
  const sourceAnswer = find(sourceQuestion.answers, {
    refId: originalAnswerRefId,
  });

  if (
    sourceAnswer.style === ANSWER_OTHER ||
    sourceQuestion.style === QUESTION_OPEN_ENDED
  ) {
    const storedQuestion =
      find(backendStoredAnswers, {
        q_ref_id: questionRefId,
      }) || {};
    const storedAnswer =
      find(storedQuestion.answers, {
        id: sourceAnswer.id,
      }) || {};

    if (storedAnswer.text) {
      return htmlAsText(storedAnswer.text);
    }
    return get(originalAnswer, 'body', '');
  }

  return originalAnswer.body;
}

function htmlAsText(html) {
  const node = document.createElement('div');
  node.textContent = decodeURIComponent(html);

  return node.innerHTML;
}

const attrPipingRegEx = /{{attrRefId:([a-z0-9-]*)}}/g;
const pipingRegEx = /{{refId:([a-z0-9-]*)(\/subRefId:([a-z0-9-]*))?}}/g;
// 1. Can transform prompt based on the current concept's attribute value (question group with attribute set).
//    e.g "Do you like the bar {{attrRefId:'attributeRefId'}}?" --> "Do you like the bar Barret?" for current
//    concept and attribute 'bar'.
// 2. Transforms prompt based on the selected answer of a previous question.
//    e.g "Hello {{refId:'questionRefId'}} {{refId:'questionRefId2'}}" --> "Hello John Doe".
//    We may have to select a specific answer of a previous question based on subRefId.
export const transformPrompt = (prompt = '') => {
  let newPrompt = prompt;

  // check attribute ref matches
  const referencedAttributes = [];
  let match;
  do {
    match = attrPipingRegEx.exec(newPrompt);
    if (match) {
      referencedAttributes.push(match);
    }
  } while (match);
  attrPipingRegEx.lastIndex = 0;
  if (referencedAttributes.length) {
    newPrompt = handleAttrRefReplacements(newPrompt, referencedAttributes);
  }

  // check question ref matches
  const referencedQuestions = [];
  do {
    match = pipingRegEx.exec(newPrompt);
    if (match) {
      referencedQuestions.push(match);
    }
  } while (match);
  pipingRegEx.lastIndex = 0;
  if (referencedQuestions.length) {
    newPrompt = handleQuestionRefReplacements(newPrompt, referencedQuestions);
  }

  // add extra sizing span for emojis
  newPrompt = newPrompt.replace(
    emojiRegex(),
    (emoji) => `<span class="emoji-text">${emoji}</span>`,
  );
  return newPrompt;
};

const handleAttrRefReplacements = (prompt, referencedAttributes) => {
  let newPrompt = prompt;

  referencedAttributes.forEach((referencedAttribute) => {
    const [textToReplace, attributeRefId] = referencedAttribute;
    const value = getAttributeValueForCurrentConcept(attributeRefId);
    newPrompt = newPrompt.replace(textToReplace, value || BLANK_PIPING_ANSWER);
  });

  return newPrompt;
};

const getAttributeValueForCurrentConcept = (attributeRefId) => {
  const { currentGroup } = Pollfish.survey;
  const currentConcept = currentGroup?.getCurrentConcept();
  if (!currentGroup || !currentConcept) {
    return '';
  }

  const concept = find(
    currentGroup.concepts,
    ({ refId }) => refId === currentConcept,
  );
  if (!concept || !concept.attributeValues) return '';

  const attributeValue = find(
    concept.attributeValues,
    ({ attributeRefId: refId }) => refId === attributeRefId,
  );
  if (!attributeValue || !attributeValue.text) return '';

  return attributeValue.text;
};

const handleQuestionRefReplacements = (prompt, referencedQuestions) => {
  let newPrompt = prompt;

  const {
    questionAnswers,
    screeningQuestionAnswers,
  } = Pollfish.store.getRespondentKnowledge();
  const allQuestionAnswers = [...screeningQuestionAnswers, ...questionAnswers];

  each(referencedQuestions, (referencedQuestion) => {
    const [textToReplace, refId, , subRefId] = referencedQuestion;
    const {
      answers,
      scorepoints,
      style,
      matrix,
    } = Pollfish.survey.getQuestionByRefId(refId);
    const userAnswered = find(allQuestionAnswers, ['questionId', refId]);

    // when using head_id or head_refId url parameter
    if (!userAnswered) {
      newPrompt = newPrompt.replace(textToReplace, BLANK_PIPING_ANSWER);
    } else {
      const backendStoredAnswers = invoke(
        Pollfish,
        'survey.getStoredAnswers',
        [],
      );
      const storedQuestion =
        find(backendStoredAnswers, (question) => question.q_ref_id === refId) ||
        {};
      let values = getAllValuesFromAnswers({
        storedQuestion,
        style,
        targetedAnswerRefId: subRefId,
        userAnswers: userAnswered.answers,
        answers,
        scorepoints,
        matrix,
        backendStoredAnswers,
      });
      values = transformPrompt(values);
      newPrompt = newPrompt.replace(textToReplace, values);
    }
  });

  return newPrompt;
};

export function escapeHTML(html = '') {
  const regexEscape = /["&'<>`]/g;
  const escapeMap = {
    '"': '&quot;',
    '&': '&amp;',
    "'": '&#x27;',
    '<': '&lt;',
    '>': '&gt;',
    '`': '&#x60;',
  };
  if (typeof html === 'string') {
    return html.replace(regexEscape, (match) => escapeMap[match]);
  }
  return html;
}

export const cleanAnswersFromUnusedProperties = (answers) =>
  answers.map((answer) =>
    omit(answer, [
      'reward_name',
      'reward_conversion',
      'reward_conversion_hash',
    ]),
  );

// checks if all answers contains only emoji in their body
// answer with emoji separated by space are considered as non only emoji
export const doesAnswersContainsOnlyEmoji = (answers = []) => {
  if (answers.length === 0) {
    return false;
  }
  const answersContainsOnlyEmoji = answers.map((answer) => {
    if (!answer.body) {
      return false;
    }
    const newAnswer = answer.body.replace(emojiRegex(), '');
    return newAnswer.length === 0;
  });
  return answersContainsOnlyEmoji.every((isOnlyEmoji) => isOnlyEmoji);
};

export const roundNumberToStringWithDecimals = (value, decimals = 0) => {
  let output = value;
  output = parseFloat(output);
  output = round(output, decimals);
  return output.toFixed(decimals);
};

export const getLocaleDate = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString();
};

export function canApplyVisibilityEvaluationOnNodeAnswers({
  refId,
  groupRefId,
  type,
  knowledge,
}) {
  if (!isNodeSupportAnswersVisibility({ type })) {
    return false;
  }

  const nodeVisibility = getNodeVisibility(knowledge, refId, groupRefId);

  if (!nodeVisibility) {
    return false;
  }

  return true;
}

export function applyVisibilityActionOnNodeAnswers({
  refId,
  groupRefId,
  type,
  list = [],
  listType = ActionVisibilityValueType.Answer,
}) {
  if (list.length === 0) {
    return list;
  }

  const knowledge = Pollfish.store.getRespondentKnowledge();

  if (
    !canApplyVisibilityEvaluationOnNodeAnswers({
      refId,
      groupRefId,
      type,
      knowledge,
    })
  ) {
    return list;
  }

  const nodeVisibility = getNodeVisibility(knowledge, refId, groupRefId) || {};
  const visibility = nodeVisibility[listType];

  // no visibility rules applied for this question
  if (!visibility) {
    return list;
  }

  return list.filter((answer) => {
    const itemRefId = answer.answerRefId || answer.refId;

    return (
      !(itemRefId in visibility) ||
      visibility[itemRefId] === VisibilityStatus.Visible
    );
  });
}

export function isSupportingKnowledgeVisibility(node) {
  if (!node) {
    return false;
  }

  return true;
}

export function shouldUpdateKnowledgeFromAdl(node) {
  if (!node || node.isScreening) {
    return false;
  }

  return true;
}

function applyVisibilityActionOnNodeColumns(node, columns) {
  if (isMatrixTypeLikert(node)) {
    return columns.map((columnsPerRow) =>
      applyVisibilityActionOnNodeAnswers({
        refId: node.refId,
        groupRefId: node.groupRefId,
        type: node.style,
        list: columnsPerRow,
        listType: ActionVisibilityValueType.Column,
      }),
    );
  }

  return applyVisibilityActionOnNodeAnswers({
    refId: node.refId,
    groupRefId: node.groupRefId,
    type: node.style,
    list: columns,
    listType: ActionVisibilityValueType.Column,
  });
}

export function getVisibleRowsAndColumns(node, rows = [], columns = []) {
  if (!isSupportingKnowledgeVisibility(node)) {
    return {
      rows,
      columns,
    };
  }

  const isMatrixLikert = isMatrixTypeLikert(node);
  const columnsPerRow = isMatrixLikert ? columns[0] : columns;
  const rowListType = isMatrix(node)
    ? ActionVisibilityValueType.Row
    : ActionVisibilityValueType.Answer;

  // 0. initialize knowledge visibility for node
  const updatedKnowledge = VisibilityService.applyForNode({
    node,
    rows,
    columns: columnsPerRow,
    respondentKnowledge: Pollfish.store.getRespondentKnowledge(),
  });

  // 1. update visibility global store
  Pollfish.store.updateRespondentKnowledgeByKey(
    KnowledgeKeys.Visibility,
    updatedKnowledge,
  );

  if (shouldUpdateKnowledgeFromAdl(node)) {
    // 2. adl evaluation for the for rows and columns
    Pollfish.store.updateKnowledgeByAdlOutcomeEvaluation(
      OutcomeType.Answer,
      node.refId,
      Pollfish.survey.props.questionnaireLogic,
    );
  }

  // 3. filter rows and column list based on visibility knowledge
  return {
    rows: applyVisibilityActionOnNodeAnswers({
      refId: node.refId,
      groupRefId: node.groupRefId,
      type: node.style,
      list: rows,
      listType: rowListType,
    }),
    columns: applyVisibilityActionOnNodeColumns(node, columns),
  };
}

// retrieve GET url parameter by name
/*eslint-disable */
export const getURLParameter = (name) =>
  decodeURI(
    (new RegExp(name + '=' + '(.+?)(&|$)').exec(location.search) || [
      ,
      null,
    ])[1],
  );
/* eslint-enable */

export function isUrlParameterEqualTo(key, value) {
  return getURLParameter(key) === value;
}

export {
  shuffleArray,
  getAnswersShuffleType,
  getScorepointShuffleType,
  getGenericShuffleType,
  getShuffledAndCarriedForwardAnswers,
  base64,
  getDecodedText,
  enrichMatrixRows,
  isMatrix,
  isMatrixTypeLikert,
  isMatrixOpenEndedFamily,
};

export default toUTF8Array;
